import { useDebounce } from "@/hooks/useDebounce";
import { AgendaItemDetails } from "@/models/prepareRecording";
import { FieldVariant, Loader, LoaderSize, Space, TableHead, TableRowModel, VerticalFlex } from "@enxcs/dap-ui-stratos";
import { MouseEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingContainer, StyledSearch, TableContentContainer, TableBodyContainer } from "./Agenda.styled";
import { createAgendaTableHeadings } from "./headers";
import { createAgendaTableRows, filterAgendaItems } from "./rows";

export interface AgendaStepProps {
  agendasList: AgendaItemDetails[];
  isLoadingAgendas: boolean;
  selectedAgendaId: string;
  onSelectAgenda: (agendaId: string) => void;
}

export const AgendaStep = ({ agendasList, isLoadingAgendas, selectedAgendaId, onSelectAgenda }: AgendaStepProps) => {
  const { t } = useTranslation();
  const [filterKey, setFilterKey] = useState("");
  const debouncedFilterKey = useDebounce(filterKey, 1000);

  const tableHeadings = useMemo(() => createAgendaTableHeadings(t), [t]);

  const filteredTableData = useMemo(() => {
    return filterAgendaItems(agendasList, debouncedFilterKey);
  }, [agendasList, debouncedFilterKey]);

  const tableData: TableRowModel[] = useMemo(() => {
    return createAgendaTableRows(filteredTableData, selectedAgendaId);
  }, [selectedAgendaId, filteredTableData]);

  const handleFilterChange = (value: string) => {
    setFilterKey(value);
    onSelectAgenda("");
  };

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isLoadingAgendas) {
      return;
    }

    const rowElement = (event.target as HTMLElement).closest('[role="row"]');
    const rowGroupElement = rowElement?.parentElement;

    if (!rowElement || !rowGroupElement) {
      return;
    }

    const rowElements = Array.from(rowGroupElement.querySelectorAll('[role="row"]'));
    const rowIndex = rowElements.indexOf(rowElement);
    const row = rowIndex >= 0 ? tableData[rowIndex] : undefined;

    if (row) {
      onSelectAgenda(row.key);
    }
  };

  return (
    <VerticalFlex $gap={Space.V8}>
      <StyledSearch
        value={filterKey}
        placeholder={t("buttons.search")}
        clearTitle={t("buttons.clear")}
        role="search"
        id="search"
        onChange={handleFilterChange}
        variant={FieldVariant.COMPACT}
      />
      <TableContentContainer role="table" onClick={handleRowClick}>
        <TableHead headings={tableHeadings} radioSelection />

        <TableBodyContainer
          className="border-none"
          radioSelection={true}
          isLoading={isLoadingAgendas}
          selectedKeys={isLoadingAgendas ? undefined : selectedAgendaId ? [selectedAgendaId] : []}
          onSelectRow={(agendaId, value) => {
            onSelectAgenda(value ? agendaId : "");
          }}
          rows={
            !isLoadingAgendas
              ? tableData
              : [
                  {
                    key: `loadingPage`,
                    cells: [
                      {
                        content: (
                          <LoadingContainer>
                            <Loader size={LoaderSize.V32} />
                          </LoadingContainer>
                        ),
                      },
                    ],
                  },
                ]
          }
        />
      </TableContentContainer>
    </VerticalFlex>
  );
};
